import { useEffect, useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Box, BottomNavigation, BottomNavigationAction, useMediaQuery } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, GitBranch, Activity, Bot, BookOpen } from 'lucide-react';
import { Sidebar } from './Sidebar';
import { MeshGradient } from './MeshGradient';
import { setMobileView } from '@/store/slices/uiSlice';
import { useAuth } from '@/hooks/useAuth';
import { ADMIN_ROLES, filterByRoles } from '@/utils/rbac';
import type { AppDispatch } from '@/store/store';

const pageVariants = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
};

interface MobileNavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  requiredRoles?: string[];
}

const mobileNavItems: MobileNavItem[] = [
  { label: 'Chat', path: '/chat', icon: <MessageSquare size={20} /> },
  { label: 'Pipelines', path: '/pipelines', icon: <GitBranch size={20} /> },
  {
    label: 'Monitor',
    path: '/monitoring',
    icon: <Activity size={20} />,
    requiredRoles: ADMIN_ROLES,
  },
  { label: 'Agents', path: '/agent-studio', icon: <Bot size={20} /> },
  { label: 'Knowledge', path: '/knowledge', icon: <BookOpen size={20} /> },
];

export function AppLayout() {
  const dispatch = useDispatch<AppDispatch>();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const filteredMobileNav = useMemo(
    () => filterByRoles(mobileNavItems, user?.roles ?? []),
    [user?.roles],
  );

  useEffect(() => {
    dispatch(setMobileView(isMobile));
  }, [isMobile, dispatch]);

  const activeMobileNav = filteredMobileNav.findIndex(
    (item) => location.pathname === item.path || location.pathname.startsWith(item.path + '/'),
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        height: '100vh',
        width: '100vw',
        overflow: 'hidden',
      }}
    >
      <MeshGradient />
      {!isMobile && <Sidebar />}
      <Box
        component="main"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          position: 'relative',
          zIndex: 1,
          // Leave room for bottom nav on mobile
          pb: isMobile ? '56px' : 0,
        }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}
          >
            <Outlet />
          </motion.div>
        </AnimatePresence>
      </Box>
      {isMobile && (
        <BottomNavigation
          value={activeMobileNav === -1 ? 0 : activeMobileNav}
          onChange={(_event, newValue: number) => {
            const item = filteredMobileNav[newValue];
            if (item) {
              void navigate(item.path);
            }
          }}
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 20,
            bgcolor: 'var(--color-bg-secondary)',
            borderTop: 1,
            borderColor: 'divider',
            '& .MuiBottomNavigationAction-root': {
              color: 'text.secondary',
              minWidth: 0,
              py: 1,
              '&.Mui-selected': {
                color: 'primary.main',
              },
            },
            '& .MuiBottomNavigationAction-label': {
              fontSize: '0.625rem',
              '&.Mui-selected': {
                fontSize: '0.625rem',
              },
            },
          }}
        >
          {filteredMobileNav.map((item) => (
            <BottomNavigationAction key={item.path} label={item.label} icon={item.icon} />
          ))}
        </BottomNavigation>
      )}
    </Box>
  );
}
